import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import moment from "moment";

export interface FetchEnquiriesParams {
  page: number;
  pageSize: number;
  sort?: string;
  order?: "asc" | "desc" | "";
  search?: string;
  filters?: any[];
}

// Fetch enquiries list
export const fetchEnquiries = createAsyncThunk(
  "enquiry/fetchEnquiries",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    if (filters && filters.length > 0) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (
            filterField &&
            filterValue !== undefined &&
            filterValue !== null
          ) {
            // --- SPECIAL HANDLING FOR ENQUIRY DATE ---
            if (
              filterField === "enquiry_date" &&
              typeof filterValue === "object"
            ) {
              const { from_date, to_date } = filterValue;

              if (from_date) params.append("enquiry_from_date", from_date);
              if (to_date) params.append("enquiry_to_date", to_date);

              return; // stop default logic
            }

            // --- SPECIAL HANDLING FOR SITE VISIT DATE ---
            if (
              filterField === "site_visit_date" &&
              typeof filterValue === "object"
            ) {
              const { from_date, to_date } = filterValue;

              if (from_date) params.append("site_visit_from_date", from_date);
              if (to_date) params.append("site_visit_to_date", to_date);

              return;
            }

            // --- SPECIAL HANDLING FOR created_at / updated_at ---
            if (
              (filterField === "created_at" || filterField === "updated_at") &&
              typeof filterValue === "object"
            ) {
              Object.entries(filterValue).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                  params.append(key, String(value));
                }
              });

              params.append(
                "filter_by",
                filterField === "created_at" ? "created" : "updated"
              );
              return;
            }

            // --- DEFAULT handling for objects (range filters) ---
            if (typeof filterValue === "object") {
              Object.entries(filterValue).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                  params.append(key, String(value));
                }
              });
              return;
            }

            // --- DEFAULT primitive filters ---
            params.append(filterField, filterValue);
          }
        }
      );
    }

    const response: any = await Axios.get(`/enquiry/?${params.toString()}`);

    return {
      data: response.data.data || response.data || [],
      rowCount: response.data.count || response.count || 0,
    };
  }
);

// Fetch enquiry by ID
export const fetchEnquiryById = createAsyncThunk(
  "enquiry/fetchEnquiryById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/enquiry/${id}/get`);
      // Handle response structure: response.data.data or response.data
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch enquiry"
      );
    }
  }
);

// Close enquiry
export const closeEnquiry = createAsyncThunk(
  "enquiry/closeEnquiry",
  async (enquiryId: string, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(`/enquiry/${enquiryId}/close/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to close enquiry"
      );
    }
  }
);

// Update enquiry stage
export const updateEnquiryStage = createAsyncThunk(
  "enquiry/updateEnquiryStage",
  async (
    { enquiryId, stage }: { enquiryId: string; stage: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.patch(`/enquiry/${enquiryId}/stage/`, {
        stage,
      });
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ||
          error.message ||
          "Failed to update enquiry stage"
      );
    }
  }
);

// Export enquiries CSV
export const exportEnquiriesCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any[];
}) => {
  try {
    console.log("Export filters:", filters);
    console.log("Export search:", search);
    const params: any = {
      export: "csv",
    };

    if (search) {
      params.append("search", search);
    }

    if (filters && filters.length > 0) {
      filters.forEach((filter: any) => {
        // Handle both destructured and direct property access
        const filterField = filter.filterField || filter.id;
        const filterValue = filter.filterValue !== undefined ? filter.filterValue : filter.value;

        if (
          filterField &&
          filterValue !== undefined &&
          filterValue !== null &&
          filterValue !== ""
        ) {
          // --- SPECIAL HANDLING FOR ENQUIRY DATE ---
          if (
            filterField === "enquiry_date" &&
            typeof filterValue === "object"
          ) {
            const { from_date, to_date } = filterValue;

            if (from_date) params.enquiry_from_date = from_date;
            if (to_date) params.enquiry_to_date = to_date;

            return; // stop default logic
          }

          // --- SPECIAL HANDLING FOR SITE VISIT DATE ---
          if (
            filterField === "site_visit_date" &&
            typeof filterValue === "object"
          ) {
            const { from_date, to_date } = filterValue;

            if (from_date) params.site_visit_from_date = from_date;
            if (to_date) params.site_visit_to_date = to_date;

            return;
          }

          // --- SPECIAL HANDLING FOR created_at / updated_at ---
          if (
            (filterField === "created_at" || filterField === "updated_at") &&
            typeof filterValue === "object"
          ) {
            Object.entries(filterValue).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                params[key] = String(value);
              }
            });

            params.filter_by =
              filterField === "created_at" ? "created" : "updated";
            return;
          }

          // --- DEFAULT handling for objects (range filters) ---
          if (typeof filterValue === "object" && filterValue !== null) {
            Object.entries(filterValue).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                params[key] = String(value);
              }
            });
            return;
          }

          // --- DEFAULT primitive filters ---
          params[filterField] = String(filterValue);
        }
      });
    }

    console.log("Export params:", params);
    
    const response = await Axios.get("/enquiry/", {
      params,
      responseType: "blob",
    });

    return {
      blob: response.data,
      filename: `enquiries_${moment().format("YYYY-MM-DD")}.csv`,
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to export enquiries"
    );
  }
};

// Fetch dropdown options for enquiry filters
export const fetchEnquiryDropdowns = createAsyncThunk(
  "enquiry/fetchDropdowns",
  async (columnId: string, { rejectWithValue }) => {
    try {
      let url = "";

      switch (columnId) {
        case "customer_name":
          url = "/enquiry/dropdown/customer_name";
          break;
        case "staff_name":
          url = "/enquiry/dropdown/staff_name";
          break;
        case "source_of_enquiry":
          url = "/enquiry/dropdown/source_of_enquiry";
          break;
        default:
          url = `/enquiry/dropdown/${columnId}`;
      }

      const response = await Axios.get(url);
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.[columnId] || [];

      return { columnId, data };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch dropdown"
      );
    }
  }
);
